import React, { memo, useMemo, forwardRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { AgGridReact } from 'ag-grid-react';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'ag-grid-community/styles/ag-grid.css';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'ag-grid-community/styles/ag-theme-quartz.css';
import {
  AvatarCellRenderer,
  RoleManagementCellRenderer,
  ActionsCellRenderer,
  StatusCellRenderer
} from '../../atoms';
import { userShape, roleShape } from '../../../../../propsShape';

const UsersTableWrapper = styled.div`
  width: 100%;
  border-radius: 8px;

  .ag-theme-quartz .ag-header-cell {
    border-right: none !important;
    border-left: none !important;
  }

  .ag-theme-quartz .ag-header-cell::after {
    display: none !important;
  }

  .ag-cell {
    display: flex;
    align-items: center;
  }

  /* Remove border between avatar and name columns */
  .ag-cell[col-id='avatar'] {
    border-right: none !important;
  }

  /* Remove border from role column */
  .ag-cell[col-id='role'] {
    border-right: none !important;
  }
`;

const AnimatedTableContainer = styled.div`
  /* Smooth animation on mount/remount */
  animation: smoothTableUpdate 0.25s cubic-bezier(0.4, 0, 0.2, 1);

  @keyframes smoothTableUpdate {
    0% {
      opacity: 0;
      transform: translateY(-4px) scale(0.98);
    }
    50% {
      opacity: 0.7;
      transform: translateY(-1px) scale(0.99);
    }
    100% {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const AgGridContainer = styled.div`
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
  border: none;

  --ag-font-size: 13px;
  --ag-font-family: var(--font-family-body);
  --ag-icon-font-color: var(--content-body);

  --ag-header-background-color: var(--wash-gray-default);
  --ag-header-foreground-color: var(--content-title);

  --ag-foreground-color: var(--content-body);
  --ag-background-color: var(--white);

  --ag-header-column-resize-handle-height: 100%;
  --ag-header-column-resize-handle-width: 1px;
  --ag-header-column-resize-handle-color: var(--border-muted);

  --ag-header-height: 40px;
  --ag-row-height: 40px;

  --ag-cell-horizontal-border: solid var(--border-muted);
  --ag-border-color: var(--border-muted);
  --ag-border-radius: 8px;
  --ag-row-border-color: var(--border-muted);

  --ag-row-hover-color: var(--elevation-mid);
  --ag-header-cell-hover-background-color: transparent;
  --ag-range-selection-border-color: var(--primary-default);

  .ag-header-cell {
    font-weight: 600;
  }

  & .ag-row.ag-row-last {
    --ag-row-border-color: transparent;
  }

  & .ag-cell.ag-column-last,
  & .ag-header-cell.ag-column-last {
    --ag-cell-horizontal-border: none;
  }

  & .ag-layout-auto-height {
    & .ag-center-cols-viewport,
    & .ag-center-cols-container {
      min-height: 40px;
    }
  }

  & .ag-overlay-no-rows-wrapper.ag-layout-auto-height {
    padding-top: 40px;
  }
`;

const UsersTable = memo(
  forwardRef(
    (
      {
        className = null,
        users,
        onDeleteUser,
        onUpdateUserEntitlement,
        currentUserRole,
        isSameUser,
        roles
      },
      forwardedRef
    ) => {
      // Create a simple key that changes when user roles change
      const tableKey = useMemo(() => {
        const roleHash = users
          .map((u) => u.memberships?.[0]?.entitlements?.[0]?.role?.id || 'none')
          .join('-');
        return `users-${roleHash}`;
      }, [users]);

      // Memoized column definitions
      const columnDefs = useMemo(
        () => [
          {
            headerName: 'Name',
            field: 'name',
            cellRenderer: AvatarCellRenderer,
            flex: 1,
            minWidth: 180,
            resizable: false,
            cellStyle: {
              display: 'flex',
              alignItems: 'center',
              padding: '8px 16px'
            }
          },
          {
            headerName: 'Email',
            field: 'email',
            flex: 1,
            minWidth: 100,
            resizable: false,
            cellStyle: {
              padding: '8px 16px'
            }
          },
          {
            headerName: 'Status',
            field: 'status',
            cellRenderer: StatusCellRenderer,
            width: 100,
            resizable: false,
            cellStyle: {
              padding: '8px 16px'
            }
          },
          {
            headerName: 'Role',
            field: 'role',
            cellRenderer: RoleManagementCellRenderer,
            cellRendererParams: {
              currentUserRole,
              roles,
              onUpdateUserEntitlement
            },
            flex: 1,
            width: 400,
            resizable: false,
            sortable: false,
            filter: false,
            cellStyle: {
              padding: '8px 16px'
            }
          },
          {
            headerName: '',
            field: 'actions',
            cellRenderer: ActionsCellRenderer,
            cellRendererParams: {
              onDeleteUser,
              isSameUser
            },
            width: 50,
            resizable: false,
            sortable: false,
            filter: false,
            cellStyle: {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0'
            }
          }
        ],
        [currentUserRole, roles, onUpdateUserEntitlement, onDeleteUser, isSameUser]
      );

      return (
        <UsersTableWrapper className={className}>
          <AnimatedTableContainer>
            <AgGridContainer className='ag-theme-quartz'>
              <AgGridReact
                key={tableKey}
                ref={forwardedRef}
                columnDefs={columnDefs}
                rowData={users}
                domLayout='autoHeight'
                enableCellTextSelection
                suppressMovableColumns
                autoSizePadding={0}
                suppressColumnVirtualisation
                suppressRowVirtualisation
              />
            </AgGridContainer>
          </AnimatedTableContainer>
        </UsersTableWrapper>
      );
    }
  )
);

UsersTable.propTypes = {
  className: PropTypes.string,
  onDeleteUser: PropTypes.func.isRequired,
  onUpdateUserEntitlement: PropTypes.func.isRequired,
  currentUserRole: PropTypes.string.isRequired,
  isSameUser: PropTypes.func.isRequired,
  roles: PropTypes.arrayOf(PropTypes.shape(roleShape)).isRequired,
  users: PropTypes.arrayOf(PropTypes.shape(userShape)).isRequired
};

export { UsersTable };
